// Socials data store

import { getAccounts, disconnectAccount } from "@/api/socials/socials.js";
import { authYoutube, getGoogleEmail } from "@/api/socials/youtube.js";
import { authFacebook, fetchFacebookData } from "@/api/socials/facebook.js";
import { getInstagramPages } from "@/api/socials/instagram.js";

import { getYoutubePlaylists } from "../../api/socials/youtube";
import { getInstagramQuota } from "../../api/socials/instagram";
import { authTwitter, getTwitterInfo } from "../../api/socials/twitter";

const state = {
  /** YouTube data */
  youtube: {
    id: "youtube",
    label: "YouTube",
    accounts: []
  },
  /** Facebook data */
  facebook: {
    id: "facebook",
    label: "Facebook/Instagram",
    accounts: [],
    groupsInited: false,
    pagesInited: false
  },
  instagram: {
    accounts: [],
    inited: false
  },
  twitter: {
    id: "twitter",
    label: "Twitter",
    accounts: []
  },
  /** Shared videos info */
  sharings: []
};

const getters = {
  getYoutubeAccountById(state) {
    return getAccountById.bind(null, state, "youtube");
  },
  getFacebookAccountById(state) {
    return getAccountById.bind(null, state, "facebook");
  },
  getTwitterAccountById(state) {
    return getAccountById.bind(null, state, "twitter");
  },
  getInstagramAccountById(state) {
    return getAccountById.bind(null, state, "instagram");
  }
};

const mutations = {
  clearAccounts(state, social) {
    state[social].accounts = [];
  },
  /** Add or change data about connected socials account */
  setAccountData(state, accountData) {
    const accounts = state[accountData.social].accounts;
    const idx = accounts.findIndex(acc => acc.id == accountData.id);

    if (idx >= 0) {
      // Change account
      accounts.splice(idx, 1, accountData);
    } else {
      // Add account
      accounts.push(accountData);
    }
  },
  /** Remove account by ID */
  removeAccount(state, { social, id }) {
    const accounts = state[social].accounts;
    const idx = accounts.findIndex(acc => acc.id == id);

    // if account exists
    if (idx >= 0) {
      accounts.splice(idx, 1);
    }
  },
  /** Set shared video data */
  setSharingData(state, data) {

    const sharings = state.sharings;
    let idx = sharings.findIndex(s => s.id == data.id);

    if (idx >= 0) {
      const oldSharing = sharings[idx];
      const newSharing = { ...oldSharing, ...data };
      // Add new data to sharing
      sharings.splice(idx, 1, newSharing);
    } else {
      // Append new sharing
      sharings.push(data);
      idx = sharings.length - 1;
    }
  },
  /** Remove shared video data by ID */
  removeSharingData(state, id) {
    const sharings = state.sharings;
    const idx = sharings.findIndex(s => s.id == id);

    // unsubscribe from sharing updates
    if (idx >= 0) {
      sharings.splice(idx, 1);
    }
  }
};

const actions = {
  async initAccounts({ commit, dispatch }) {
    const accounts = await getAccounts();

    for (const acc of accounts) {
      commit("setAccountData", acc);
      dispatch("initAccountLabel", acc);
    }
  },
  async initAccountLabel({ state, commit }, account) {
    let label = "Unknown";
    switch (account.social) {
      case "youtube":
        label = await getGoogleEmail(account.id);
        break;
      case "facebook":
        label = (await fetchFacebookData(account.id, "info")).name;
        break;
      case "twitter":
        label = (await getTwitterInfo(account.id)).name;
        break;
      default:
        console.error(`Unknown social: ${account.social}`);
    }

    /** get actual account data in case if it has changed by other methods */
    const actualAccount = getAccountById(state, account.social, account.id);
    if (actualAccount) {
      commit("setAccountData", { ...actualAccount, label, sync: true });
    }
  },
  async addAccount({ state, commit, dispatch }, social) {
    switch (social) {
      case "youtube":
        await authYoutube();
        break;
      case "facebook":
        await authFacebook();
        break;
      case "twitter":
        await authTwitter();
        break;
      default:
        console.error(`Unknown social: ${social}`);
        return;
    }

    /** get accounts for only connected social */
    const accounts = (await getAccounts()).filter(acc => acc.social == social);

    /** current set of IDs */
    const existingIds = new Set(state[social].accounts.map(({ id }) => id));

    /**
     * Update only new accounts to prevent data clear, but not
     * break after first hit, because here may be other updates
     */
    for (const acc of accounts) {
      if (!existingIds.has(acc.id)) {
        commit("setAccountData", acc);
        dispatch("initAccountLabel", acc);
      }
    }
  },
  async deleteAccount({ commit }, { social, id }) {
    await disconnectAccount(social, id);
    commit("removeAccount", { social, id });
  },
  async updateAccounts({ commit, dispatch }, social) {
    commit("clearAccounts", social);
    const accounts = (await getAccounts()).filter(acc => acc.social == social);

    for (const acc of accounts) {
      commit("setAccountData", acc);
      dispatch("initAccountLabel", acc);
    }
  },
  initAllYoutubePlaylists({ state, dispatch }) {
    for (const { id } of state.youtube.accounts) {
      dispatch("initYoutubePlaylists", id);
    }
  },
  async initYoutubePlaylists({ state, commit }, id) {
    const playlists = await getYoutubePlaylists(id);
    const actualAccount = getAccountById(state, "youtube", id);
    actualAccount.playlists = playlists;
    commit("setAccountData", actualAccount);
  },
  initAllFacebookGroups({ state, dispatch }) {
    for (const { id } of state.facebook.accounts) {
      dispatch("initFacebookGroups", id);
    }
    state.facebook.groupsInited = true;
  },
  async initFacebookGroups({ state, commit }, id) {
    const { data: group } = await fetchFacebookData(id, "groups");
    const actualAccount = getAccountById(state, "facebook", id);

    actualAccount.groups = group;
    commit("setAccountData", actualAccount);
  },
  initAllFacebookPages({ state, dispatch }) {
    for (const { id } of state.facebook.accounts) {
      dispatch("initFacebookPages", id);
    }
    state.facebook.pagesInited = true;
  },
  async initFacebookPages({ state, commit }, id) {
    const { data: pages } = await fetchFacebookData(id, "accounts");
    const actualAccount = getAccountById(state, "facebook", id);

    actualAccount.pages = pages;
    commit("setAccountData", actualAccount);
  },
  initAllInstagramPages({ state, dispatch }) {
    for (const { id } of state.facebook.accounts) {
      dispatch("initInstagramPages", id);
    }
    state.instagram.inited = true;
  },
  async initInstagramPages({ commit }, facebookId) {
    commit("clearAccounts", "instagram");
    const pages = await getInstagramPages(facebookId);
    for (const instaPage of pages) {
      const userId = instaPage.id;
      const pageId = instaPage.metadata.facebookPageId;
      const quota = await getInstagramQuota(pageId, userId);
      const newInstaPage = {
        ...instaPage,
        quota
      };
      commit("setAccountData", newInstaPage);
    }
  },
  /**
   * Add new sharing or change existing
   * @param {Object} sharing Sharing data (target, status, etc.)
   */
  setSharing({ commit }, sharing) {
    commit("setSharingData", sharing);
  },
  /**
   * Remove share data from store
   * @param {Number} sharingId Removing share info ID
   */
  excludeSharing({ commit }, sharingId) {
    commit("removeSharingData", sharingId);
  }
};

function getAccountById(state, social, id) {
  return state[social].accounts.find(acc => acc.id == id);
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
